import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BarChart3, Brain, CheckCircle, Crosshair, Eye, Layers,
  Link2, Lock, RefreshCcw, Shield, TrendingUp, Zap,
} from 'lucide-react';
import { Badge, Button, Card, CardHeader, CardTitle, PageSpinner } from '@/components/ui';
import { api } from '@/lib/api/client';
import { unwrap } from '@/lib/api/envelope';
import { Endpoints } from '@/lib/api/endpoints';
import type { ResponseEnvelope } from '@/types/api';
import { cn } from '@/lib/utils/cn';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ProductConfig {
  productCode: string;
  displayName: string;
  shortDescription: string;
  longDescription: string;
  category: string;
  enabled: boolean;
  defaultDeductionAmount: number;
  billingUnit: string;
}

interface ClientProductConfig {
  productCode: string;
  displayName: string;
  shortDescription: string;
  category: string;
  productGloballyEnabled: boolean;
  clientEnabled: boolean;
  effectiveDeductionAmount: number;
  billingUnit: string;
}

// ── Product icons ─────────────────────────────────────────────────────────────

const PRODUCT_ICONS: Record<string, React.ReactNode> = {
  VERIFY:              <CheckCircle className="h-5 w-5" />,
  VERIFY_PLUS:         <Zap className="h-5 w-5" />,
  SIGNAL:              <Crosshair className="h-5 w-5" />,
  SHIELD:              <Shield className="h-5 w-5" />,
  DETECT:              <Eye className="h-5 w-5" />,
  TRUST:               <BarChart3 className="h-5 w-5" />,
  STUCK:               <TrendingUp className="h-5 w-5" />,
  STUCK_PLUS:          <RefreshCcw className="h-5 w-5" />,
  STUCK_PLUS_REVEAL:   <Lock className="h-5 w-5" />,
  CONNECT:             <Link2 className="h-5 w-5" />,
  CONNECT_ENRICHMENT:  <Brain className="h-5 w-5" />,
};

// ── Category styling ──────────────────────────────────────────────────────────

const CATEGORY_COLORS: Record<string, string> = {
  VERIFICATION:          'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300',
  FRAUD_PROTECTION:      'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300',
  RISK_INTELLIGENCE:     'bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300',
  RISK_SCORING:          'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  RECOVERY_INTELLIGENCE: 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300',
  CRM_INTELLIGENCE:      'bg-cyan-100 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-300',
  TARGETING_INTELLIGENCE:'bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300',
  INTERNAL_ACTION:       'bg-slate-100 text-slate-500 dark:bg-slate-500/10 dark:text-slate-400',
  JOURNEY_INTELLIGENCE:  'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300',
  DEVICE_INTELLIGENCE:   'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  NETWORK_INTELLIGENCE:  'bg-cyan-100 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-300',
};

const CATEGORY_LABELS: Record<string, string> = {
  VERIFICATION:           'Verification',
  FRAUD_PROTECTION:       'Fraud Protection',
  RISK_INTELLIGENCE:      'Risk Intelligence',
  RISK_SCORING:           'Risk Scoring',
  RECOVERY_INTELLIGENCE:  'Recovery Intelligence',
  CRM_INTELLIGENCE:       'CRM Intelligence',
  TARGETING_INTELLIGENCE: 'Targeting Intelligence',
  INTERNAL_ACTION:        'Internal Billing Action',
  JOURNEY_INTELLIGENCE:   'Journey Intelligence',
  DEVICE_INTELLIGENCE:    'Device Intelligence',
  NETWORK_INTELLIGENCE:   'Network Intelligence',
};

// ── API ───────────────────────────────────────────────────────────────────────

async function fetchProducts(): Promise<ProductConfig[]> {
  const res = await api.get<ResponseEnvelope<ProductConfig[]>>(Endpoints.adminProducts.list);
  return unwrap(res);
}

async function fetchClientProducts(clientId: string): Promise<ClientProductConfig[]> {
  const res = await api.get<ResponseEnvelope<ClientProductConfig[]>>(
    Endpoints.adminProducts.clientProducts(clientId));
  return unwrap(res);
}

async function updateClientProduct(clientId: string, productCode: string, enabled: boolean, deduction?: number) {
  const res = await api.put<ResponseEnvelope<unknown>>(
    Endpoints.adminProducts.updateClientProduct(clientId, productCode),
    { enabled, customDeductionAmount: deduction ?? null });
  unwrap(res);
}

// ── Client product config panel ───────────────────────────────────────────────

function ClientProductPanel({ clientId }: { clientId: string }) {
  const queryClient = useQueryClient();
  const [pendingDeduction, setPendingDeduction] = useState<Record<string, string>>({});

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'client-products', clientId],
    queryFn: () => fetchClientProducts(clientId),
    staleTime: 30_000,
  });

  const toggleMut = useMutation({
    mutationFn: ({ code, enabled, deduction }: { code: string; enabled: boolean; deduction?: number }) =>
      updateClientProduct(clientId, code, enabled, deduction),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'client-products', clientId] }),
  });

  if (isLoading) return <div className="py-4 text-center text-xs text-fg-muted">Loading products…</div>;
  if (isError) return <div className="py-4 text-center text-xs text-rose-500">Failed to load client products.</div>;

  return (
    <div className="space-y-2 pt-2">
      {data?.map((p) => {
        const isAction = p.category === 'INTERNAL_ACTION';
        return (
          <div key={p.productCode}
               className={cn('flex items-center gap-3 rounded-xl border border-border px-4 py-3',
                 isAction ? 'bg-surface-subtle/60' : 'bg-surface-muted/40')}>
            <div className={cn('flex h-8 w-8 items-center justify-center rounded-lg text-xs',
              p.clientEnabled ? 'bg-primary-100 text-primary-600 dark:bg-primary-500/15 dark:text-primary-400'
                : 'bg-surface-subtle text-fg-muted')}>
              {PRODUCT_ICONS[p.productCode] ?? <Layers className="h-4 w-4" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-fg">{p.displayName}</p>
                {isAction && (
                  <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500 dark:bg-slate-500/10 dark:text-slate-400">
                    Billing Action
                  </span>
                )}
              </div>
              <p className="text-xs text-fg-muted">{p.effectiveDeductionAmount} credit · {p.billingUnit}</p>
            </div>
            <input
              type="number"
              min={1}
              value={pendingDeduction[p.productCode] ?? p.effectiveDeductionAmount}
              onChange={(e) => setPendingDeduction((prev) => ({ ...prev, [p.productCode]: e.target.value }))}
              className="w-16 rounded-lg border border-border bg-surface px-2 py-1 text-center text-xs text-fg focus:outline-none focus:ring-1 focus:ring-primary-500/30"
            />
            <button
              onClick={() => toggleMut.mutate({
                code: p.productCode,
                enabled: !p.clientEnabled,
                deduction: pendingDeduction[p.productCode]
                  ? parseInt(pendingDeduction[p.productCode]!, 10) : undefined,
              })}
              disabled={toggleMut.isPending}
              className={cn('relative h-5 w-9 rounded-full transition-colors focus:outline-none disabled:opacity-50',
                p.clientEnabled ? 'bg-primary-500' : 'bg-fg-subtle/30')}
            >
              <span className={cn('absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform',
                p.clientEnabled ? 'translate-x-4' : 'translate-x-0.5')} />
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function AdminProductsPage() {
  const [clientIdInput, setClientIdInput] = useState('');
  const [activeClientId, setActiveClientId] = useState<string | null>(null);

  const { data: products, isLoading } = useQuery({
    queryKey: ['admin', 'products'],
    queryFn: fetchProducts,
    staleTime: 5 * 60_000,
  });

  const mainProducts = products?.filter((p) => p.category !== 'INTERNAL_ACTION') ?? [];
  const actionProducts = products?.filter((p) => p.category === 'INTERNAL_ACTION') ?? [];

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-xl font-semibold text-fg">Product Catalog</h1>
        <p className="mt-0.5 text-sm text-fg-muted">
          Manage Authmatech products and client access configuration.
        </p>
      </div>

      {/* Global product catalog */}
      <Card>
        <CardHeader>
          <CardTitle>Global Product Catalog</CardTitle>
        </CardHeader>
        {isLoading ? (
          <div className="py-8"><PageSpinner /></div>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {mainProducts.map((p) => (
                <div key={p.productCode}
                     className="rounded-xl border border-border bg-surface p-4 shadow-card dark:shadow-card-dark">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <div className={cn('flex h-9 w-9 items-center justify-center rounded-xl',
                        p.enabled ? 'bg-primary-100 text-primary-600 dark:bg-primary-500/15 dark:text-primary-400'
                          : 'bg-surface-subtle text-fg-muted')}>
                        {PRODUCT_ICONS[p.productCode] ?? <Layers className="h-5 w-5" />}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-fg">{p.displayName}</p>
                        <span className={cn('inline-block rounded-full px-2 py-0.5 text-[10px] font-medium',
                          CATEGORY_COLORS[p.category] ?? 'bg-slate-100 text-slate-600')}>
                          {CATEGORY_LABELS[p.category] ?? p.category}
                        </span>
                      </div>
                    </div>
                    <Badge tone={p.enabled ? 'success' : 'neutral'} dot>
                      {p.enabled ? 'Active' : 'Disabled'}
                    </Badge>
                  </div>
                  <p className="mt-3 text-xs text-fg-muted leading-relaxed">{p.shortDescription}</p>
                  <div className="mt-3 flex items-center justify-between text-xs text-fg-muted">
                    <span className="font-medium text-fg">{p.defaultDeductionAmount} credit</span>
                    <span>{p.billingUnit}</span>
                  </div>
                </div>
              ))}
            </div>

            {actionProducts.length > 0 && (
              <div className="mt-6">
                <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-fg-subtle">
                  Internal Billing Actions
                </p>
                <p className="mb-3 text-xs text-fg-muted">
                  These are chargeable sub-actions billed to the client balance. They are not surfaced as standalone products in the client portal.
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {actionProducts.map((p) => (
                    <div key={p.productCode}
                         className="flex items-start gap-3 rounded-xl border border-border bg-surface-subtle/50 p-4">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-surface-muted text-fg-muted">
                        {PRODUCT_ICONS[p.productCode] ?? <Layers className="h-4 w-4" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-fg">{p.displayName}</p>
                        <p className="mt-0.5 text-xs text-fg-muted leading-relaxed">{p.shortDescription}</p>
                        <div className="mt-2 flex items-center justify-between text-xs">
                          <span className="font-semibold text-fg">{p.defaultDeductionAmount} credits · {p.billingUnit}</span>
                          <Badge tone={p.enabled ? 'success' : 'neutral'} dot>
                            {p.enabled ? 'Active' : 'Off'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Per-client product management */}
      <Card>
        <CardHeader>
          <CardTitle>Client Product Access</CardTitle>
        </CardHeader>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Enter Client ID…"
            value={clientIdInput}
            onChange={(e) => setClientIdInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && clientIdInput.trim()) setActiveClientId(clientIdInput.trim()); }}
            className="flex-1 rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg placeholder:text-fg-muted focus:outline-none focus:ring-2 focus:ring-primary-500/30"
          />
          <Button size="sm" onClick={() => clientIdInput.trim() && setActiveClientId(clientIdInput.trim())}>
            Load
          </Button>
        </div>
        {activeClientId && (
          <div className="mt-4">
            <p className="mb-2 text-xs font-medium text-fg-muted">
              Products for client: <span className="font-mono text-fg">{activeClientId}</span>
            </p>
            <ClientProductPanel clientId={activeClientId} />
          </div>
        )}
      </Card>
    </div>
  );
}
